import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  type Comment,
  type CommentsQueryData,
} from 'hb-react/frontoffice/apps/CommentsV2/types';

// Helper function that goes through the paginated query state and applies
// given action to mapping of `data` property of each page. Mapping function
// can be modified with optional `fn` attribute.
const traverseQueryPagesData =
  (action: (comment: Comment) => any, fn = 'map') =>
  (prevState: CommentsQueryData | undefined) => {
    if (!prevState) {
      return;
    }

    return {
      ...prevState,
      pages: prevState.pages.map((page) => ({
        ...page,
        // Comments are stored in `data` attribute
        data: page.data[fn](action),
      })),
    };
  };

export const useCommentsQueryHandlers = (queryKey) => {
  const queryClient = useQueryClient();

  return useMemo(
    () => ({
      // Adds a new comment at the top of the first page.
      handleNewComment: ({ newComment }: { newComment: Comment }) => {
        queryClient.setQueryData<CommentsQueryData | undefined>(
          queryKey,
          (prevState) => {
            if (!prevState) {
              queryClient.invalidateQueries({ queryKey });

              return prevState;
            }

            const firstPage = prevState.pages[0];
            const lastPage = prevState.pages[prevState.pages.length - 1];
            const nextTotalCount = (lastPage.total_count ?? 0) + 1;
            const nextTotalCountWithReplies =
              (lastPage.total_count_with_replies ?? 0) + 1;

            const nextFirstPage = {
              ...firstPage,
              total_count: nextTotalCount,
              total_count_with_replies: nextTotalCountWithReplies,
              data: [newComment, ...firstPage.data],
            };

            return {
              ...prevState,
              pages: prevState.pages.map((page, i) =>
                i === 0
                  ? nextFirstPage
                  : {
                      ...page,
                      total_count: nextTotalCount,
                      total_count_with_replies: nextTotalCountWithReplies,
                    },
              ),
            };
          },
        );
      },

      // Removes comment with given ID from query data.
      handleDeleteComment: ({
        id,
        repliesCount,
      }: {
        id: number;
        repliesCount?: number;
      }) => {
        const action = (comment) => comment.id !== id;

        queryClient.setQueryData<CommentsQueryData | undefined>(
          queryKey,
          (prevState) => {
            // Remove comment from the list
            const nextState = traverseQueryPagesData(
              action,
              'filter',
            )(prevState);

            if (!nextState) {
              return nextState;
            }

            const lastPage = nextState.pages[nextState.pages.length - 1];
            const nextTotalCount = (lastPage.total_count ?? 1) - 1;
            const nextTotalCountWithReplies =
              (lastPage.total_count_with_replies ?? 1) -
              ((repliesCount ?? 0) + 1);

            // Update total_count for each page
            return {
              ...nextState,
              pages: nextState.pages.map((page) => ({
                ...page,
                total_count: nextTotalCount,
                total_count_with_replies: nextTotalCountWithReplies,
              })),
            };
          },
        );
      },

      // Updates query data with given comment
      handleUpdateComment: ({
        comment: updatedComment,
        countDelta: delta,
      }: {
        comment: Comment;
        countDelta?: number;
      }) => {
        queryClient.setQueryData<CommentsQueryData | undefined>(
          queryKey,
          (prevState) => {
            if (!prevState) {
              return prevState;
            }

            const lastPage = prevState.pages[prevState.pages.length - 1];
            const nextTotalCountWithReplies =
              (lastPage.total_count_with_replies ?? 0) + (delta ?? 0);

            // update only the comment with the given ID
            const action = (comment) =>
              comment.id === updatedComment.id ? updatedComment : comment;

            const nextState = {
              ...prevState,
              pages: prevState.pages.map((page) => ({
                ...page,
                data: page.data.map(action),
                total_count_with_replies: nextTotalCountWithReplies,
              })),
            };

            return nextState;
          },
        );
      },

      // Updates likes object for comment in query data with given ID.
      handleToggleLike: ({
        commentId,
        newLikes,
      }: {
        commentId: number;
        newLikes: unknown;
      }) => {
        const action = (comment) =>
          comment.id === commentId
            ? {
                ...comment,
                likes: newLikes,
              }
            : comment;

        queryClient.setQueryData(queryKey, traverseQueryPagesData(action));
      },

      // Updates votes object for the comment in query data with given ID.
      handleVote: ({
        commentId,
        state,
        score,
      }: {
        commentId: number;
        score: unknown;
        state: unknown;
      }) => {
        const action = (comment) =>
          comment.id === commentId
            ? {
                ...comment,
                votes: { state, score },
              }
            : comment;

        queryClient.setQueryData(queryKey, traverseQueryPagesData(action));
      },
    }),
    [queryClient, queryKey],
  );
};
